import { createContext, useContext } from "react";

import { LocalesEmployersRecord } from "../../../constants/locale";
import type { CompanyFormSubmitError } from "../../../util/companies-utils";
import { WifService } from "../../../types/wif-companies";

// State

export type CompanyFormState = {
  isLoading: boolean;
  isDone: boolean;
  errorMessage?: CompanyFormSubmitError;
  totalSteps: number;
  currentStep: number;
  companyId: string;
  companyName: string;

  companyPhone: string;
  companyEmail: string;
  companyServices: WifService[];
  companyDescription: Partial<LocalesEmployersRecord>;
  companyWebsite: Partial<LocalesEmployersRecord>;
  companyLogo: File | undefined;
  companyTerms: boolean;
  companyTerms2: boolean;
};

export const companyFormDefaultState: CompanyFormState = {
  isLoading: false,
  isDone: false,
  errorMessage: undefined,
  totalSteps: 2,
  currentStep: 1,
  companyId: "",
  companyName: "",
  companyDescription: {},
  companyWebsite: {},
  companyEmail: "",
  companyLogo: undefined,
  companyPhone: "",
  companyServices: [],
  companyTerms: false,
  companyTerms2: false,
};

// Context

export const StateContext = createContext<CompanyFormState>(
  companyFormDefaultState
);

export type CompanyFormDispatch = React.Dispatch<CompanyFormPayload>;
export const DispatchContext = createContext<CompanyFormDispatch>(() => {});

// Hooks

export const useCompanyFormDispatch = () => useContext(DispatchContext);
export const useCompanyFormState = () => useContext(StateContext);

// Helpers

export function companyFormReducer(
  state: CompanyFormState,
  payload: CompanyFormPayload
): CompanyFormState {
  return { ...state, ...payload(state) };
}

// Actions

type PartialCompanyFormState = Partial<CompanyFormState>;
type CompanyFormPayload = (state: CompanyFormState) => PartialCompanyFormState;
type CompanyFormAction<T = never> = (value?: T) => CompanyFormPayload;

const submit: CompanyFormAction = () => {
  return () => {
    return { isDone: true, isLoading: false, errorMessage: undefined };
  };
};

const cancel: CompanyFormAction = () => {
  return () => companyFormDefaultState;
};

const startLoading: CompanyFormAction = () => {
  return () => ({ isLoading: true, errorMessage: undefined });
};
const stopLoading: CompanyFormAction = () => {
  return () => ({ isLoading: false, errorMessage: undefined });
};

const setErrorMessage: CompanyFormAction<CompanyFormSubmitError> = (
  errorMessage
) => {
  return () => ({ errorMessage, isLoading: false });
};

const setCompanyId: CompanyFormAction<{
  companyId: string;
  companyName?: string;
}> = (payload) => {
  if (!payload?.companyId) return () => ({});

  return () => ({
    companyId: payload.companyId,
    companyName: payload.companyName,
    currentStep: 2,
  });
};

const setValue: CompanyFormAction<Partial<CompanyFormState>> = (partial) => {
  return () => partial ?? {};
};

const setWebsite: CompanyFormAction<Partial<LocalesEmployersRecord>> = (
  partial
) => {
  return (state) => {
    const newState: CompanyFormState = {
      ...state,
      companyWebsite: { ...state.companyWebsite, ...partial },
    };

    return newState;
  };
};

const setDescription: CompanyFormAction<Partial<LocalesEmployersRecord>> = (
  partial
) => {
  return (state) => {
    const newState: CompanyFormState = {
      ...state,
      companyDescription: { ...state.companyDescription, ...partial },
    };

    return newState;
  };
};

export const companyFormActions = {
  cancel,
  setCompanyId,
  resetCompanyId: cancel,
  setValue,
  setWebsite,
  setDescription,
  submit,
  startLoading,
  stopLoading,
  setErrorMessage,
} as const;
